import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getPostComments, addCommentToBlogPost } from '../../services/firebase/commentsService';
import './CommentsSection.css';

const CommentsSection = ({ postId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  // Load comments on mount
  useEffect(() => {
    loadComments();
  }, [postId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getPostComments(postId);
      if (result.success) {
        setComments(result.data);
      } else {
        // Only show error if it's not a permission error (permission errors will show empty comments)
        if (!result.error?.includes('permission')) {
          setError(result.error || 'Failed to load comments');
        }
        setComments([]);
      }
    } catch (err) {
      console.error('Error loading comments:', err);
      // Silently fail for permission errors
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) {
      setError('Comment cannot be empty');
      return;
    }

    if (!user) {
      setError('You must be logged in to comment');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const result = await addCommentToBlogPost(postId, {
        content: commentText,
        author: user.displayName || user.email,
        authorId: user.uid,
        authorAvatar: user.photoURL || '',
      });

      if (result.success) {
        setCommentText('');
        setSuccess('Comment submitted successfully! It will appear after admin approval.');
        setTimeout(() => setSuccess(''), 5000);
        // Reload comments
        loadComments();
      } else {
        setError(result.error || 'Failed to submit comment');
      }
    } catch (err) {
      setError('Error submitting comment');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="comments-section">
      <div className="comments-container">
        <h3 className="comments-title">💬 Comments ({comments.length})</h3>

        {error && (
          <div className="comment-alert alert-error">
            {error}
            <button className="alert-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {success && (
          <div className="comment-alert alert-success">
            {success}
            <button className="alert-close" onClick={() => setSuccess('')}>×</button>
          </div>
        )}

        {/* Comment Form */}
        {user ? (
          <form className="comment-form" onSubmit={handleSubmitComment}>
            <div className="comment-form-header">
              <h4>Add Your Comment</h4>
              <span className="char-count">{commentText.length}/1000</span>
            </div>

            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Share your thoughts on this article..."
              className="comment-textarea"
              maxLength="1000"
              rows="4"
            />

            <button
              type="submit"
              className="btn-submit-comment"
              disabled={submitting || !commentText.trim()}
            >
              {submitting ? 'Submitting...' : 'Post Comment'}
            </button>
          </form>
        ) : (
          <div className="comment-login-prompt">
            <p>Please <a href="/login">log in</a> to leave a comment.</p>
          </div>
        )}

        {/* Comments List */}
        <div className="comments-list">
          {loading ? (
            <div className="loading">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="no-comments">
              <p>No comments yet. Be the first to comment!</p>
            </div>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className="comment-item">
                <div className="comment-header">
                  <div className="comment-author-info">
                    {comment.authorAvatar && (
                      <img
                        src={comment.authorAvatar}
                        alt={comment.author}
                        className="comment-author-avatar"
                      />
                    )}
                    <div className="comment-author-details">
                      <p className="comment-author-name">{comment.author}</p>
                      <p className="comment-date">
                        {new Date(comment.createdAt?.toDate?.() || comment.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="comment-content">
                  <p>{comment.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default CommentsSection;
